import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSimulation } from '../context/SimulationContext';
import { useAuth } from '../context/AuthContext';
import ProtocolEvaluationPanel from '../components/ProtocolEvaluationPanel';

function ProtocolEvaluationPanelWrapper({ sessionId }) {
  const { user } = useAuth();
  const isInstructor = ['instructor', 'admin', 'super_admin'].includes(user?.role);
  return <ProtocolEvaluationPanel sessionId={sessionId} isInstructor={isInstructor} />;
}
import { reportAPI, simulationsAPI } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import TimelineViewer from '../components/TimelineViewer';
import { 
  CheckCircle, 
  XCircle, 
  Lightbulb, 
  Clock,
  ChartLine,
  Users,
  Megaphone,
  Trophy,
  ArrowRight,
  Spinner,
  FilePdf,
  DownloadSimple,
  ListBullets,
  ClipboardText,
  Warning
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function DebriefingPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { 
    session, 
    scenario, 
    debriefing, 
    generateDebriefing, 
    resetSimulation,
    formatTime 
  } = useSimulation();
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [timelineAnalysis, setTimelineAnalysis] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  // v10.8.3 — Capa XR en el debriefing
  const [xrEvents, setXrEvents] = useState([]);
  const [xrMvpReport, setXrMvpReport] = useState(null);

  useEffect(() => {
    loadDebriefing();
  }, [sessionId]);

  // v10.8.3 — Cargar eventos XR + MVP report en paralelo
  useEffect(() => {
    const targetSessionId = sessionId || session?.id;
    if (!targetSessionId) return;
    const token = localStorage.getItem('token');
    const base = process.env.REACT_APP_BACKEND_URL;
    fetch(`${base}/api/xr/sessions/${targetSessionId}/timeline`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => setXrEvents(d?.events || []))
      .catch(() => setXrEvents([]));
    fetch(`${base}/api/xr/sessions/${targetSessionId}/mvp-report`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => setXrMvpReport(d))
      .catch(() => setXrMvpReport(null));
  }, [sessionId, session?.id]);

  // Cargar análisis del timeline
  useEffect(() => {
    const loadTimelineAnalysis = async () => {
      // Usar sessionId de URL params o session.id del contexto
      const targetSessionId = sessionId || session?.id;
      if (!targetSessionId) return;
      
      setLoadingTimeline(true);
      try {
        const response = await simulationsAPI.getTimelineAnalysis(targetSessionId);
        setTimelineAnalysis(response.data);
      } catch (error) {
        console.error('Error loading timeline analysis:', error);
        // No mostrar error toast aquí, es opcional
      } finally {
        setLoadingTimeline(false);
      }
    };
    
    if ((sessionId || session?.id) && !generating) {
      loadTimelineAnalysis();
    }
  }, [session?.id, sessionId, generating]);

  const loadDebriefing = async () => {
    if (!debriefing) {
      const targetSessionId = sessionId || session?.id;
      if (!targetSessionId) {
        setLoading(false);
        return;
      }
      setGenerating(true);
      try {
        await generateDebriefing(targetSessionId);
        toast.success('Debriefing generado correctamente');
      } catch (error) {
        toast.error('Error al generar debriefing');
      } finally {
        setGenerating(false);
      }
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Use sessionId from URL params or session context
    const targetSessionId = sessionId || session?.id;
    if (!targetSessionId) {
      toast.error('No hay sesión activa');
      return;
    }

    setDownloadingPdf(true);
    try {
      const response = await reportAPI.downloadPDF(targetSessionId);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `SimuClinic_Informe_${targetSessionId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado correctamente');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(error.response?.data?.detail || 'Error al descargar PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleNewSimulation = () => {
    resetSimulation();
    navigate('/app/scenarios');
  };

  const handleViewHistory = () => {
    navigate('/app/dashboard');
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  if (loading || generating) {
    return (
      <>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <Spinner size={48} className="animate-spin text-blue-600" />
          <p className="text-slate-500">
            {generating ? 'Generando análisis de debriefing con IA...' : 'Cargando...'}
          </p>
        </div>
      </>
    );
  }

  const analysis = debriefing?.analysis;

  return (
    <>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <Badge className="mb-4 bg-purple-100 text-purple-800">Fase de Debriefing</Badge>
          <h1 className="text-3xl font-bold font-['Chivo'] text-slate-900 mb-2" data-testid="debriefing-title">
            Análisis de Desempeño
          </h1>
          <p className="text-slate-500">
            {scenario?.nombre}
          </p>
        </div>

        {/* Evaluación por protocolo */}
        <ProtocolEvaluationPanelWrapper sessionId={sessionId} />

        {/* Score Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className={`text-center ${getScoreBg(analysis?.puntuacion_total || 0)}`} data-testid="total-score-card">
            <CardContent className="pt-6">
              <Trophy size={32} className={`mx-auto mb-2 ${getScoreColor(analysis?.puntuacion_total || 0)}`} />
              <p className="text-sm text-slate-600 mb-1">Puntuación Total</p>
              <p className={`text-4xl font-bold font-['Chivo'] ${getScoreColor(analysis?.puntuacion_total || 0)}`}>
                {analysis?.puntuacion_total || 0}
              </p>
              <p className="text-xs text-slate-500">/ 100</p>
            </CardContent>
          </Card>

          <Card data-testid="technical-score-card">
            <CardContent className="pt-6 text-center">
              <ChartLine size={32} className="mx-auto mb-2 text-blue-600" />
              <p className="text-sm text-slate-600 mb-1">Competencias Técnicas</p>
              <p className="text-3xl font-bold font-['Chivo'] text-blue-600">
                {analysis?.puntuacion_tecnica || 0}
              </p>
              <Progress value={analysis?.puntuacion_tecnica || 0} className="mt-2" />
            </CardContent>
          </Card>

          <Card data-testid="nontechnical-score-card">
            <CardContent className="pt-6 text-center">
              <Users size={32} className="mx-auto mb-2 text-purple-600" />
              <p className="text-sm text-slate-600 mb-1">Comp. No Técnicas</p>
              <p className="text-3xl font-bold font-['Chivo'] text-purple-600">
                {analysis?.puntuacion_no_tecnica || 0}
              </p>
              <Progress value={analysis?.puntuacion_no_tecnica || 0} className="mt-2" />
            </CardContent>
          </Card>

          {/* Timeline Score Card */}
          <Card data-testid="timeline-score-card" className={timelineAnalysis?.overall_score >= 75 ? 'bg-green-50' : timelineAnalysis?.overall_score >= 50 ? 'bg-yellow-50' : 'bg-red-50'}>
            <CardContent className="pt-6 text-center">
              <Warning size={32} className={`mx-auto mb-2 ${timelineAnalysis?.overall_score >= 75 ? 'text-green-600' : timelineAnalysis?.overall_score >= 50 ? 'text-yellow-600' : 'text-red-600'}`} />
              <p className="text-sm text-slate-600 mb-1">Adherencia Protocolo</p>
              <p className={`text-3xl font-bold font-['Chivo'] ${timelineAnalysis?.overall_score >= 75 ? 'text-green-600' : timelineAnalysis?.overall_score >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                {loadingTimeline ? '--' : (timelineAnalysis?.overall_score || 0)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {timelineAnalysis?.adverse_events || 0} desviaciones
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs: Summary, Timeline, OSCE */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
          <TabsList className="bg-slate-100 w-full justify-start">
            <TabsTrigger value="summary" className="gap-2 data-[state=active]:bg-white">
              <ClipboardText size={16} />
              Resumen
            </TabsTrigger>
            <TabsTrigger value="timeline" className="gap-2 data-[state=active]:bg-white">
              <ListBullets size={16} />
              Timeline de Eventos
              {timelineAnalysis?.adverse_events > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {timelineAnalysis.adverse_events}
                </Badge>
              )}
            </TabsTrigger>
            {xrEvents.length > 0 && (
              <TabsTrigger value="xr" className="gap-2 data-[state=active]:bg-white" data-testid="tab-debrief-xr">
                <ListBullets size={16} />
                Capa XR
                <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] p-0 flex items-center justify-center text-xs bg-cyan-100 text-cyan-700">
                  {xrEvents.length}
                </Badge>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Tab: Resumen */}
          <TabsContent value="summary" className="mt-6 space-y-6">
            {/* Time */}
            <Card>
              <CardContent className="flex items-center justify-center gap-4 py-4">
                <Clock size={24} className="text-slate-400" />
                <span className="text-slate-600">Tiempo de simulación:</span>
                <span className="font-vital text-2xl text-slate-900" data-testid="total-time">
                  {formatTime(analysis?.tiempo_total || 0)}
                </span>
              </CardContent>
            </Card>

            {/* Strengths */}
            {debriefing?.instructor_interventions?.length > 0 && (
              <Card className="border-purple-200" data-testid="instructor-interventions-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-purple-700 text-base">
                    Intervenciones del Instructor ({debriefing.instructor_interventions.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    {debriefing.instructor_interventions.map((iv, i) => (
                      <li key={iv.id || i} className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-1.5">
                        <Badge variant="outline" className="text-purple-700 border-purple-300 capitalize">
                          {(iv.intervention_type || 'intervención').replace(/_/g, ' ')}
                        </Badge>
                        <span className="text-slate-700 font-medium">{iv.instructor_name}</span>
                        {iv.added_minutes && <span className="text-slate-500">+{iv.added_minutes} min</span>}
                        {iv.reason && <span className="text-slate-500">— {iv.reason}</span>}
                        <span className="text-xs text-slate-400 ml-auto">
                          {iv.timestamp ? new Date(iv.timestamp).toLocaleTimeString('es-CL') : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <Card className="border-green-200" data-testid="strengths-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-700">
                  <CheckCircle size={24} weight="fill" />
                  Fortalezas Identificadas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {analysis?.fortalezas?.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-green-800" data-testid={`strength-${i}`}>
                      <CheckCircle size={18} className="mt-0.5 flex-shrink-0" weight="fill" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Errors */}
            <Card className="border-red-200" data-testid="errors-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <XCircle size={24} weight="fill" />
                  Áreas de Mejora
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {analysis?.errores?.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-red-800" data-testid={`error-${i}`}>
                      <XCircle size={18} className="mt-0.5 flex-shrink-0" weight="fill" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Recommendations */}
            <Card className="border-blue-200" data-testid="recommendations-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <Lightbulb size={24} weight="fill" />
                  Recomendaciones
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {analysis?.recomendaciones?.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-blue-800" data-testid={`recommendation-${i}`}>
                      <Lightbulb size={18} className="mt-0.5 flex-shrink-0" weight="fill" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Soft Skills */}
            <Card data-testid="soft-skills-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Megaphone size={24} />
                  Evaluación de Competencias Blandas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-medium text-slate-700 mb-2">Comunicación</h4>
                    <p className="text-sm text-slate-600">{analysis?.comunicacion || 'Sin evaluación'}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-700 mb-2">Liderazgo</h4>
                    <p className="text-sm text-slate-600">{analysis?.liderazgo || 'Sin evaluación'}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-700 mb-2">Trabajo en Equipo</h4>
                    <p className="text-sm text-slate-600">{analysis?.trabajo_equipo || 'Sin evaluación'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Timeline de Eventos */}
          <TabsContent value="timeline" className="mt-6">
            <Card className="bg-slate-900 border-slate-700">
              <CardContent className="p-6">
                {loadingTimeline ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-4">
                    <Spinner size={48} className="animate-spin text-blue-500" />
                    <p className="text-slate-400">Cargando análisis del timeline...</p>
                  </div>
                ) : timelineAnalysis ? (
                  <TimelineViewer analysis={timelineAnalysis} />
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <Warning size={48} className="mx-auto mb-4 text-slate-500" />
                    <p>No hay datos de timeline disponibles para esta sesión</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* v10.8.3 — Capa XR: eventos AR/MR + MVP report */}
          {xrEvents.length > 0 && (
            <TabsContent value="xr" className="mt-6 space-y-6">
              {xrMvpReport && xrMvpReport.mvp_type === 'anatomical_id' && (
                <Card className="border-cyan-200">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">Reporte MVP · {xrMvpReport.scenario_name}</CardTitle>
                      {xrMvpReport.totals.accuracy_pct >= 80 && (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/xr/sessions/${sessionId || session?.id}/certificate?_t=${Date.now()}`}
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              const token = localStorage.getItem('token');
                              const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/xr/sessions/${sessionId || session?.id}/certificate`, {
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'error');
                              const blob = await res.blob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `certificado_xr_${(sessionId || session?.id).slice(0,8)}.pdf`;
                              a.click();
                              window.URL.revokeObjectURL(url);
                            } catch (err) { console.error(err); }
                          }}
                          className="text-xs px-3 py-1.5 rounded-md bg-cyan-600 hover:bg-cyan-500 text-white font-semibold flex items-center gap-1"
                          data-testid="download-certificate-btn"
                        >
                          Descargar certificado PDF
                        </a>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-4 gap-3">
                      <div className="p-3 rounded-md bg-cyan-50 text-center">
                        <div className="text-[10px] uppercase text-cyan-600 font-bold">Completitud</div>
                        <div className="text-2xl font-bold text-cyan-700">{xrMvpReport.totals.completion_pct}%</div>
                      </div>
                      <div className="p-3 rounded-md bg-emerald-50 text-center">
                        <div className="text-[10px] uppercase text-emerald-600 font-bold">Precisión</div>
                        <div className="text-2xl font-bold text-emerald-700">{xrMvpReport.totals.accuracy_pct}%</div>
                      </div>
                      <div className="p-3 rounded-md bg-red-50 text-center">
                        <div className="text-[10px] uppercase text-red-600 font-bold">Errores</div>
                        <div className="text-2xl font-bold text-red-700">{xrMvpReport.totals.incorrect}</div>
                      </div>
                      <div className="p-3 rounded-md bg-slate-50 text-center">
                        <div className="text-[10px] uppercase text-slate-500 font-bold">Tiempo</div>
                        <div className="text-2xl font-bold text-slate-700">{Math.round((xrMvpReport.totals.elapsed_ms || 0) / 1000)}s</div>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs font-bold uppercase text-slate-500 tracking-widest mb-2">Detalle por zona</div>
                      {Object.entries(xrMvpReport.per_zone || {}).map(([zone, r]) => (
                        <div key={zone} className="flex items-center gap-3 p-2 rounded bg-slate-50 border border-slate-100" data-testid={`mvp-zone-${zone}`}>
                          <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700 min-w-[80px] text-center">{zone}</span>
                          <span className="text-xs text-slate-600 flex-1">Intentos: <strong>{r.attempts}</strong></span>
                          <span className="text-xs text-emerald-600">✓ {r.correct}</span>
                          <span className="text-xs text-red-600">✗ {r.incorrect}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              <Card className="bg-slate-900 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-slate-100 text-base">Eventos XR (intercalados con timeline clínico)</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-1 max-h-[500px] overflow-y-auto" data-testid="xr-debrief-events">
                    {xrEvents.map((e, i) => {
                      const p = e.payload || {};
                      const t = p.target || {};
                      const ts = e.created_at || e.timestamp;
                      return (
                        <div key={e.id || i} className="flex items-center gap-3 p-2 rounded bg-slate-800/50 border border-slate-700">
                          <span className="text-slate-400 font-mono text-[10px] w-8 text-right">#{e.sequence_number}</span>
                          <span className="font-mono text-cyan-300 text-xs min-w-[130px]">{e.event_type.replace('xr:', '')}</span>
                          <span className="text-slate-200 text-xs flex-1 truncate">
                            {Object.entries(t).filter(([, v]) => v !== null && v !== '' && v !== undefined).slice(0, 4).map(([k, v]) => `${k}=${v}`).join(' · ')}
                          </span>
                          {p.correct === true && <span className="text-[10px] text-emerald-400 font-bold">✓</span>}
                          {p.correct === false && <span className="text-[10px] text-red-400 font-bold">✗</span>}
                          <span className="text-slate-500 text-[10px]">{ts ? new Date(ts).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
          <Button 
            variant="outline" 
            onClick={handleDownloadPDF}
            disabled={downloadingPdf}
            className="gap-2"
            data-testid="download-pdf-btn"
          >
            {downloadingPdf ? (
              <Spinner size={18} className="animate-spin" />
            ) : (
              <FilePdf size={18} />
            )}
            Descargar PDF
          </Button>
          <Button variant="outline" onClick={handleViewHistory} data-testid="view-history-btn">
            Ver Historial
          </Button>
          <Button onClick={handleNewSimulation} className="gap-2" data-testid="new-simulation-btn">
            Nueva Simulación
            <ArrowRight size={18} />
          </Button>
        </div>
      </div>
    </>
  );
}
